// Marketplace-only LogRocket session bridge. It is initialized by auth.js from the
// account layout; leadgen and other bundles neither initialize nor read this cache.
//
// The question this answers is "was this borrower being recorded when the handoff
// happened, and by which session?". Two different facts decide that, and the v2 entry
// conflated them into one field:
//
//   capturedAt - how old the URL itself is. LogRocket rolls a session over after about
//                half an hour, so a URL older than that is no longer this recording.
//   lastSeenAt - when this document last proved it was alive. Returning to the app is
//                only continuous inside LogRocket's ~2 minute away window, and "away"
//                is measured from the moment the page stopped being on screen.
//
// Using capturedAt for both meant a READY page open for 5.5 minutes threw away its own
// cache on an ordinary reload, even though the borrower was gone for under a second.
// Now departure touches lastSeenAt, so a long-lived page reload stays continuous while a
// genuinely abandoned entry still expires.

import {
    canCapturePrincipalEvidence,
    durableLocalStorage,
    LOGROCKET_PRINCIPAL_KEY,
    LOGROCKET_SESSION_CACHE_KEY_PREFIX,
    normalizeLogRocketId as normalizeId,
    removeStorageItem,
    resolveLogRocketPrincipalState,
} from './logrocketPrincipal';

// The storage namespace and the account-switch rule are owned by logrocketPrincipal so
// they can run from a synchronous head script, ahead of identify. Re-exported here because
// this module is still where the marketplace cache is read from.
export { LOGROCKET_PRINCIPAL_KEY, LOGROCKET_SESSION_CACHE_KEY_PREFIX };

export const LOGROCKET_SESSION_CHANNEL_NAME = 'marketplace:logrocket-session:v3';
export const LOGROCKET_LEGACY_SESSION_CACHE_KEY = 'lr:last-session';

/** Superseded schemas. Their entries cannot be upgraded: they have no liveness fact. */
export const LOGROCKET_LEGACY_CACHE_KEY_PREFIXES = [
    'marketplace:logrocket-session:v1',
    'marketplace:logrocket-session:v2',
];

/** How long after this document stopped being visible a return is still continuous. */
export const LOGROCKET_SESSION_CACHE_TTL_MS = 2 * 60 * 1000;

/** Hard bound on the session itself, whatever any tab claims about liveness. */
export const LOGROCKET_ACTIVE_TAB_SESSION_TTL_MS = 30 * 60 * 1000;

export const LOGROCKET_SESSION_RESPONSE_TIMEOUT_MS = 500;

/** Shared with the server vocabulary (MarketplaceSale::SNAPSHOT_SOURCES). */
export const LOGROCKET_SESSION_SOURCE = {
    LIVE_PAGE: 'live_page',
    RECENT_PAGE: 'recent_page',
    ACTIVE_TAB: 'active_tab',
};

const LOGROCKET_SESSION_URL_PREFIX = 'https://app.logrocket.com/';
const LOGROCKET_SESSION_URL_MAX_LENGTH = 2048;
const CACHE_VERSION = 3;
const REQUEST_TYPE = 'marketplace-logrocket-session-request';
const RESPONSE_TYPE = 'marketplace-logrocket-session-response';
const SESSION_PATH_PATTERN = /^\/(.+)\/s\/([^/]+)(?:\/[A-Za-z0-9._~-]*)*$/;

function globalObject() {
    if (typeof globalThis !== 'undefined') {
        return globalThis;
    }

    if (typeof window !== 'undefined') {
        return window;
    }

    return null;
}

function defaultStorage(runtime = globalObject()) {
    // Durable browser storage only (see durableLocalStorage): the #5873 in-memory shim
    // dies with the document, so entries written to it are gone before any later page
    // or another tab could read them back. Honest "no cache" beats one that cannot keep
    // its own promises about surviving a navigation.
    return durableLocalStorage(runtime);
}

function normalizeProject(value) {
    return typeof value === 'string' && value.trim() !== ''
        ? value.trim().replace(/^\/+|\/+$/g, '').toLowerCase()
        : null;
}

function normalizeContext(context) {
    const userId = normalizeId(context?.userId);
    const loanId = normalizeId(context?.loanId);

    return userId && loanId
        ? { userId, loanId, project: normalizeProject(context?.project) }
        : null;
}

/**
 * The identity inside a player link: which project recorded it and which session it is.
 * Mirrors App\Rules\LogRocketSessionUrl so both ends agree on what "this project's
 * session" means, including the recording index the SDK appends (".../s/6-abc/0").
 */
export function parseLogRocketSessionUrl(value) {
    if (
        typeof value !== 'string'
        || !value.startsWith(LOGROCKET_SESSION_URL_PREFIX)
        || value.length > LOGROCKET_SESSION_URL_MAX_LENGTH
        || value !== value.trim()
    ) {
        return null;
    }

    try {
        const URLConstructor = globalObject()?.URL;
        const parsed = new URLConstructor(value);

        if (
            parsed.protocol !== 'https:'
            || parsed.hostname !== 'app.logrocket.com'
            || parsed.port !== ''
            || parsed.username !== ''
            || parsed.password !== ''
        ) {
            return null;
        }

        const match = SESSION_PATH_PATTERN.exec(parsed.pathname);

        return match
            ? { url: value, project: match[1].toLowerCase(), sessionId: match[2] }
            : null;
    } catch (error) {
        return null;
    }
}

export function isValidLogRocketSessionUrl(value, project = null) {
    const identity = parseLogRocketSessionUrl(value);
    const expected = normalizeProject(project);

    return !!identity && (expected === null || identity.project === expected);
}

export function getLogRocketSessionCacheKey(context) {
    const normalized = normalizeContext(context);

    return normalized
        ? `${LOGROCKET_SESSION_CACHE_KEY_PREFIX}:${normalized.userId}:${normalized.loanId}`
        : null;
}

function isUsableTimestamp(value, now) {
    return Number.isFinite(value) && Number.isFinite(now) && value <= now;
}

/**
 * An entry is usable only if BOTH facts hold: the document stopped being visible
 * recently (lastSeenAt, the away window) and the session itself is still young enough to
 * be the same recording (capturedAt, the 30-minute bound).
 */
function validateEntry(entry, context, now, { awayWindowMs, maxSessionAgeMs = LOGROCKET_ACTIVE_TAB_SESSION_TTL_MS } = {}) {
    const normalized = normalizeContext(context);

    if (
        !normalized
        || !entry
        || entry.version !== CACHE_VERSION
        || normalizeId(entry.userId) !== normalized.userId
        || normalizeId(entry.loanId) !== normalized.loanId
        || !isUsableTimestamp(entry.capturedAt, now)
        || !isUsableTimestamp(entry.lastSeenAt, now)
        || entry.lastSeenAt < entry.capturedAt
    ) {
        return null;
    }

    const identity = parseLogRocketSessionUrl(entry.url);

    if (!identity) {
        return null;
    }

    // A same-host link from another LogRocket project is somebody else's recording.
    if (normalized.project !== null && identity.project !== normalized.project) {
        return null;
    }

    if (normalized.project === null && normalizeProject(entry.project) !== identity.project) {
        return null;
    }

    if (now - entry.capturedAt >= maxSessionAgeMs) {
        return null;
    }

    if (Number.isFinite(awayWindowMs) && now - entry.lastSeenAt >= awayWindowMs) {
        return null;
    }

    return {
        version: CACHE_VERSION,
        userId: normalized.userId,
        loanId: normalized.loanId,
        url: identity.url,
        project: identity.project,
        sessionId: identity.sessionId,
        source: entry.source === LOGROCKET_SESSION_SOURCE.LIVE_PAGE
            ? LOGROCKET_SESSION_SOURCE.LIVE_PAGE
            : LOGROCKET_SESSION_SOURCE.RECENT_PAGE,
        capturedAt: entry.capturedAt,
        lastSeenAt: entry.lastSeenAt,
    };
}

function readEntry(context, { now = Date.now(), storage = defaultStorage(), awayWindowMs } = {}) {
    const key = getLogRocketSessionCacheKey(context);

    if (!key || !storage) {
        return null;
    }

    try {
        const raw = storage.getItem(key);

        if (!raw) {
            return null;
        }

        const entry = validateEntry(JSON.parse(raw), context, now, { awayWindowMs });

        if (!entry) {
            removeStorageItem(storage, key);
        }

        return entry;
    } catch (error) {
        removeStorageItem(storage, key);

        return null;
    }
}

function writeEntry(entry, context, storage) {
    const key = getLogRocketSessionCacheKey(context);

    if (!key || !storage) {
        return;
    }

    try {
        storage.setItem(key, JSON.stringify(entry));
    } catch (error) {
        // Quota/blocked storage means the live SDK path remains the only source.
    }
}

function createEntry(sessionUrl, context, now, previous = null) {
    const normalized = normalizeContext(context);
    const identity = parseLogRocketSessionUrl(sessionUrl);

    if (
        !normalized
        || !identity
        || !Number.isFinite(now)
        || (normalized.project !== null && identity.project !== normalized.project)
    ) {
        return null;
    }

    // The same session seen again is not a new capture: keeping the original capturedAt
    // is what makes the 30-minute bound measure the session, not the last page view.
    //
    // Identity is the session id alone. The URL carries a per-visit ?t= playback offset,
    // so requiring the whole URL to match meant one recording reset its own capturedAt
    // every time the offset moved - which is every read - and the bound it was supposed to
    // enforce never bound anything. A genuinely rolled session has a new id and still
    // resets it.
    const sameSession = previous && previous.sessionId === identity.sessionId;

    return {
        version: CACHE_VERSION,
        userId: normalized.userId,
        loanId: normalized.loanId,
        url: identity.url,
        project: identity.project,
        sessionId: identity.sessionId,
        source: LOGROCKET_SESSION_SOURCE.LIVE_PAGE,
        capturedAt: sameSession ? previous.capturedAt : now,
        lastSeenAt: now,
    };
}

function purgeLegacyAndForeignEntries(storage, context, now) {
    const normalized = normalizeContext(context);

    if (!storage || !normalized) {
        return;
    }

    removeStorageItem(storage, LOGROCKET_LEGACY_SESSION_CACHE_KEY);

    try {
        const keys = [];

        for (let index = 0; index < storage.length; index++) {
            const key = storage.key(index);

            if (typeof key !== 'string') {
                continue;
            }

            const isLegacy = LOGROCKET_LEGACY_CACHE_KEY_PREFIXES.some(prefix => key.startsWith(`${prefix}:`));

            if (isLegacy || key.startsWith(`${LOGROCKET_SESSION_CACHE_KEY_PREFIX}:`)) {
                keys.push({ key, isLegacy });
            }
        }

        keys.forEach(({ key, isLegacy }) => {
            // A v1/v2 entry has no liveness fact at all, so it can never be upgraded into
            // one - it is dropped rather than reinterpreted as fresh.
            if (isLegacy) {
                removeStorageItem(storage, key);

                return;
            }

            try {
                const entry = JSON.parse(storage.getItem(key) || 'null');
                const isCurrentUser = normalizeId(entry?.userId) === normalized.userId;
                const isFresh = validateEntry(
                    entry,
                    { userId: entry?.userId, loanId: entry?.loanId, project: normalized.project },
                    now,
                    { awayWindowMs: null }
                );

                if (!isCurrentUser || !isFresh) {
                    removeStorageItem(storage, key);
                }
            } catch (error) {
                removeStorageItem(storage, key);
            }
        });
    } catch (error) {
        // Some storage shims do not expose length/key; normal reads remain guarded.
    }
}

function broadcastChannelConstructor(runtime, override) {
    if (override !== undefined) {
        return override;
    }

    try {
        return runtime?.BroadcastChannel || null;
    } catch (error) {
        return null;
    }
}

function contextsMatch(left, right) {
    const normalizedLeft = normalizeContext(left);
    const normalizedRight = normalizeContext(right);

    return normalizedLeft
        && normalizedRight
        && normalizedLeft.userId === normalizedRight.userId
        && normalizedLeft.loanId === normalizedRight.loanId;
}

/**
 * Initialize the account-tab writer/responder. Missing context is deliberately a no-op,
 * which keeps auth.js safe on signup-bank-info and prevents any leadgen behavior change.
 */
export function initializeLogRocketSessionCache(context, options = {}) {
    const runtime = options.runtime === undefined ? globalObject() : options.runtime;
    const storage = options.storage === undefined ? defaultStorage(runtime) : options.storage;

    let logRocket = null;

    try {
        logRocket = runtime?.LogRocket;
    } catch (error) {
        // A blocked SDK means this page owns no current recording.
    }

    // The head result is reused rather than recalculated. This per-document latch is a
    // correctness boundary: when its cut succeeded but the marker write failed, a second
    // cut here would clear the identity the layout just established. If the head entry did
    // not run, this remains the defense-in-depth owner and resolves the boundary once.
    // Raw user context is intentional: a signed-in principal with no loan is still
    // identified, so it still has to end the previous person's recording.
    const principalState = resolveLogRocketPrincipalState(runtime, storage, logRocket, context?.userId);
    const normalized = normalizeContext(context);

    if (!normalized || !canCapturePrincipalEvidence(principalState)) {
        // Unsafe/unknown pages own no evidence: no SDK URL reads, cache writes, lifecycle
        // listeners or BroadcastChannel responder. Marketplace navigation remains intact.
        return () => {};
    }

    const now = options.now || Date.now;
    const BroadcastChannelConstructor = broadcastChannelConstructor(runtime, options.BroadcastChannel);
    let currentEntry = null;
    let channel = null;

    purgeLegacyAndForeignEntries(storage, normalized, now());

    if (!logRocket || typeof logRocket.getSessionURL !== 'function') {
        return () => {};
    }

    // Start from what this browser already recorded for this user and loan. Beginning
    // empty meant the first SDK read below stamped capturedAt = now onto a recording that
    // may already have been 29 minutes old, so the 30-minute bound measured this page load
    // instead of the session, and a stale entry could be renewed indefinitely by reloads.
    // Away-time is deliberately not a filter here - the entry is not being used as
    // evidence yet, only as the previous state of the same session - while the age bound
    // inside validateEntry still throws out anything too old to be that session.
    currentEntry = readEntry(normalized, { now: now(), storage, awayWindowMs: null });

    const rememberCurrentSession = sessionUrl => {
        const entry = createEntry(sessionUrl, normalized, now(), currentEntry);

        if (!entry) {
            return null;
        }

        currentEntry = entry;
        writeEntry(entry, normalized, storage);

        return entry;
    };

    /** Read the SDK synchronously - the only value that proves what is recording NOW. */
    const refreshFromSdk = () => {
        try {
            return rememberCurrentSession(logRocket.sessionURL);
        } catch (error) {
            return null;
        }
    };

    /**
     * This document is going out of view. Refresh from the SDK while we still can and
     * stamp the moment we stopped being visible: that stamp, not the capture time, is
     * what a returning page measures its away window against.
     */
    const touchLiveness = () => {
        if (!refreshFromSdk() && currentEntry) {
            currentEntry = { ...currentEntry, lastSeenAt: now() };
            writeEntry(currentEntry, normalized, storage);
        }
    };

    const openChannel = () => {
        if (channel || typeof BroadcastChannelConstructor !== 'function') {
            return;
        }

        try {
            channel = new BroadcastChannelConstructor(LOGROCKET_SESSION_CHANNEL_NAME);
            channel.onmessage = event => {
                const message = event?.data;

                if (
                    message?.type !== REQUEST_TYPE
                    || typeof message.requestId !== 'string'
                    || !contextsMatch(message, normalized)
                ) {
                    return;
                }

                // Answer from the LIVE SDK, not from whatever was cached when this tab
                // loaded: a tab that has since rolled to a new session must say so.
                refreshFromSdk();

                const entry = validateEntry(currentEntry, normalized, now(), { awayWindowMs: null });

                if (entry) {
                    channel.postMessage({
                        type: RESPONSE_TYPE,
                        requestId: message.requestId,
                        entry: { ...entry, source: LOGROCKET_SESSION_SOURCE.ACTIVE_TAB },
                    });
                }
            };
        } catch (error) {
            channel = null;
        }
    };

    const closeChannel = () => {
        try {
            channel?.close();
        } catch (error) {
            // Cleanup is best-effort during page teardown/tests.
        }

        channel = null;
    };

    openChannel();
    refreshFromSdk();

    try {
        logRocket.getSessionURL(rememberCurrentSession);
    } catch (error) {
        // LogRocket can never affect account navigation or marketplace handoff.
    }

    const onVisibilityChange = () => {
        // Hidden is not gone: a backgrounded tab still owns a live recording and must
        // keep answering. Only its liveness stamp moves, so if it never comes back the
        // entry ages out on its own.
        if (runtime?.document?.visibilityState === 'hidden') {
            touchLiveness();
        }
    };

    const onPageHide = () => {
        // The document IS going away. Stamp liveness first, then stop answering: a
        // bfcached page must not keep proving tab liveness it no longer has.
        touchLiveness();
        closeChannel();
    };

    const onPageShow = event => {
        // Restored from bfcache: the channel was closed on the way out, so it has to be
        // re-registered, and the SDK re-read in case the session rolled while away.
        if (event?.persisted) {
            openChannel();
            refreshFromSdk();
        }
    };

    const listeners = [
        ['visibilitychange', onVisibilityChange],
        ['pagehide', onPageHide],
        ['pageshow', onPageShow],
    ];

    listeners.forEach(([name, handler]) => {
        try {
            // visibilitychange is a document event everywhere; the others are window
            // events. Registering both ways keeps this working against the real DOM and
            // against a minimal test runtime.
            runtime?.addEventListener?.(name, handler);
            runtime?.document?.addEventListener?.(name, handler);
        } catch (error) {
            // Missing lifecycle events only cost freshness, never correctness.
        }
    });

    return () => {
        listeners.forEach(([name, handler]) => {
            try {
                runtime?.removeEventListener?.(name, handler);
                runtime?.document?.removeEventListener?.(name, handler);
            } catch (error) {
                // Test/page cleanup remains best-effort.
            }
        });

        closeChannel();
    };
}

/**
 * The persisted entry, usable only while the away window since this browser last had the
 * page visible has not elapsed. Longer reuse requires an active-tab answer below.
 */
export function readCachedLogRocketSessionCandidate(context, options = {}) {
    const entry = readEntry(context, {
        ...options,
        awayWindowMs: options.awayWindowMs ?? LOGROCKET_SESSION_CACHE_TTL_MS,
    });

    return entry
        ? { url: entry.url, source: LOGROCKET_SESSION_SOURCE.RECENT_PAGE, capturedAt: entry.capturedAt }
        : null;
}

export function readCachedLogRocketSessionUrl(context, options = {}) {
    return readCachedLogRocketSessionCandidate(context, options)?.url || null;
}

/**
 * Ask every still-open, same-user/same-loan account tab for its current SDK session and
 * take the freshest valid answer.
 *
 * Taking the FIRST answer made the winner a race between tabs; with several open, which
 * recording became sale evidence depended on scheduling. Collecting until the timeout and
 * ranking by capture time makes it deterministic. The promise always resolves and never
 * delays the handoff's own navigation.
 */
export function requestActiveLogRocketSessionCandidate(context, options = {}) {
    const normalized = normalizeContext(context);

    if (!normalized) {
        return Promise.resolve(null);
    }

    const runtime = options.runtime === undefined ? globalObject() : options.runtime;
    const now = options.now || Date.now;
    const storage = options.storage === undefined ? defaultStorage(runtime) : options.storage;
    const BroadcastChannelConstructor = broadcastChannelConstructor(runtime, options.BroadcastChannel);

    if (typeof BroadcastChannelConstructor !== 'function') {
        return Promise.resolve(readCachedLogRocketSessionCandidate(normalized, { now: now(), storage }));
    }

    const timeoutMs = options.timeoutMs ?? LOGROCKET_SESSION_RESPONSE_TIMEOUT_MS;
    const setTimeoutFunction = options.setTimeout || setTimeout;
    const clearTimeoutFunction = options.clearTimeout || clearTimeout;
    const random = options.random || Math.random;

    return new Promise(resolve => {
        let channel = null;
        let timeout = null;
        let settled = false;
        let best = null;
        const requestId = `${now()}-${random()}`;

        const finish = () => {
            if (settled) {
                return;
            }

            settled = true;

            if (timeout !== null) {
                clearTimeoutFunction(timeout);
            }

            try {
                channel?.close();
            } catch (error) {
                // Closing a failed channel is best-effort.
            }

            resolve(best
                ? { url: best.url, source: LOGROCKET_SESSION_SOURCE.ACTIVE_TAB, capturedAt: best.capturedAt }
                : readCachedLogRocketSessionCandidate(normalized, { now: now(), storage }));
        };

        try {
            channel = new BroadcastChannelConstructor(LOGROCKET_SESSION_CHANNEL_NAME);
            channel.onmessage = event => {
                const message = event?.data;

                if (settled || message?.type !== RESPONSE_TYPE || message.requestId !== requestId) {
                    return;
                }

                const entry = validateEntry(message.entry, normalized, now(), { awayWindowMs: null });

                if (!entry) {
                    return;
                }

                // Freshest capture wins; liveness breaks a tie between two tabs that
                // report the same session.
                if (
                    !best
                    || entry.capturedAt > best.capturedAt
                    || (entry.capturedAt === best.capturedAt && entry.lastSeenAt > best.lastSeenAt)
                ) {
                    best = entry;
                }
            };
            timeout = setTimeoutFunction(finish, timeoutMs);
            channel.postMessage({
                type: REQUEST_TYPE,
                requestId,
                userId: normalized.userId,
                loanId: normalized.loanId,
            });
        } catch (error) {
            finish();
        }
    });
}

export function requestActiveLogRocketSessionUrl(context, options = {}) {
    return requestActiveLogRocketSessionCandidate(context, options).then(candidate => candidate?.url || null);
}
